import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Cpu, Info } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { FeedbackForm } from './FeedbackForm';
import { ExportButton } from './ExportButton';
import { SignalDetail } from './SignalDetail';

interface Analysis {
  id: number;
  topic: string;
  content: string;
  llm_provider: string;
  created_at: string;
  event_type?: string | null;
  supporting_signals?: number[];
}

function extractTopLine(content: string): string {
  const match = content.match(/## Top Line\n([\s\S]*?)(?=\n## |$)/);
  return match ? match[1].trim().slice(0, 320) : content.slice(0, 320);
}

function renderMarkdownSections(content: string) {
  const sections = content.split(/\n(?=## )/);
  return sections.map((section, i) => {
    const newlineIdx = section.indexOf('\n');
    const heading = newlineIdx === -1 ? section : section.slice(0, newlineIdx);
    const body = newlineIdx === -1 ? '' : section.slice(newlineIdx + 1).trim();
    const isHeader = heading.startsWith('## ');
    const title = isHeader ? heading.replace('## ', '') : null;

    return (
      <div key={i} style={{ marginBottom: 'var(--space-5)' }}>
        {title && (
          <h3 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-sm-size)',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
            marginBottom: 'var(--space-2)',
            paddingBottom: 'var(--space-1)',
            borderBottom: '1px solid var(--color-border)',
          }}>{title}</h3>
        )}
        {body && (
          <div style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-md-size)',
            lineHeight: 'var(--text-body-md-leading)',
            color: 'var(--color-text-secondary)',
            whiteSpace: 'pre-wrap',
          }}>{body}</div>
        )}
      </div>
    );
  });
}

export function AnalysisViewer() {
  const [active, setActive] = useState<Analysis | null>(null);
  const [sigId, setSigId] = useState<number | null>(null);
  const { data, loading } = useApi<{ analyses: Analysis[] }>('/api/analyses?days=30');

  const analyses = data?.analyses ?? [];
  // The aggregated Global & Maldives overview is generated on every refresh and
  // pinned at the top. The list is ordered newest-first, so the first match is
  // the latest overview.
  const overview = analyses.find((a) => a.event_type === 'overview') ?? null;
  const rest = analyses.filter((a) => a !== overview);

  return (
    <div className="stack" style={{ gap: 'var(--space-8)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <span className="eyebrow">AI Synthesis</span>
          <h1 className="t-headline-md" style={{ marginTop: 'var(--space-2)' }}>Intelligence Analyses</h1>
          {overview && (
            <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--color-text-muted)', marginTop: 'var(--space-1)' }}>
              Last updated: {new Date(overview.created_at).toLocaleString('en-GB', { timeZone: 'Indian/Maldives', day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })} MVT
            </p>
          )}
        </div>
        <ExportButton />
      </div>

      {loading && (
        <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>Loading analyses…</p>
      )}

      {!loading && analyses.length === 0 && (
        <div className="card card-accent" data-accent="neutral" style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
          <Info size={18} strokeWidth={1.75} style={{ color: 'var(--color-text-muted)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <div className="card-title" style={{ marginBottom: 'var(--space-1)' }}>No analyses yet</div>
            <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>
              Trigger the daily pipeline from the Diagnosis page (Admin → Run Analysis), or wait for the scheduled cron to run.
              Analyses are generated automatically once signals are detected.
            </p>
          </div>
        </div>
      )}

      {/* Pinned daily overview */}
      {overview && (
        <button
          onClick={() => setActive(overview)}
          style={{ all: 'unset', cursor: 'pointer', display: 'block' }}
        >
          <div className="card card-accent" data-accent="warning">
            <div className="card-eyebrow">Daily Overview · Global &amp; Maldives</div>
            <div className="card-title">{overview.topic}</div>
            <p className="card-body" style={{ whiteSpace: 'pre-wrap' }}>
              {extractTopLine(overview.content)}…
            </p>
            <div className="card-footer" style={{ justifyContent: 'space-between' }}>
              <span className="chip" data-tone="neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Cpu size={11} strokeWidth={1.75} />
                {overview.llm_provider}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                {new Date(overview.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
              </span>
            </div>
          </div>
        </button>
      )}

      {/* Analysis cards */}
      <div className="grid-2" style={{ gap: 'var(--space-4)' }}>
        {rest.map((a) => (
          <button
            key={a.id}
            onClick={() => setActive(a)}
            style={{ all: 'unset', cursor: 'pointer', display: 'block' }}
          >
            <div className="card card-accent" data-accent="info" style={{ height: '100%' }}>
              <div className="card-eyebrow">Analysis</div>
              <div className="card-title">{a.topic}</div>
              <p className="card-body" style={{ flex: 1 }}>
                {a.content.slice(0, 160)}…
              </p>
              <div className="card-footer" style={{ justifyContent: 'space-between' }}>
                <span className="chip" data-tone="neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Cpu size={11} strokeWidth={1.75} />
                  {a.llm_provider}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                  {new Date(a.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Detail dialog — Radix Dialog with Halo styling */}
      <Dialog.Root open={!!active} onOpenChange={(open) => { if (!open) setActive(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="halo-dialog-overlay" />
          <Dialog.Content className="halo-dialog-content">
            {active && (
              <>
                <div className="row-between" style={{ marginBottom: 'var(--space-4)' }}>
                  <div>
                    <span className="eyebrow" style={{ marginBottom: 'var(--space-1)', display: 'block' }}>Intelligence Brief</span>
                    <Dialog.Title asChild>
                      <h2 className="t-title-md">{active.topic}</h2>
                    </Dialog.Title>
                  </div>
                  <Dialog.Close asChild>
                    <button className="btn btn-tertiary btn-icon btn-sm" aria-label="Close">
                      <X size={16} strokeWidth={1.75} />
                    </button>
                  </Dialog.Close>
                </div>

                <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-5)' }}>
                  <span className="chip" data-tone="neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Cpu size={11} strokeWidth={1.75} />
                    {active.llm_provider}
                  </span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                    {new Date(active.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>

                <hr style={{ borderColor: 'var(--color-border)', marginBottom: 'var(--space-5)' }} />

                <div>
                  {renderMarkdownSections(active.content)}
                </div>

                {active.supporting_signals && active.supporting_signals.length > 0 && (
                  <div style={{ marginTop: 'var(--space-5)' }}>
                    <span className="t-label-sm" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
                      Based on {active.supporting_signals.length} signal{active.supporting_signals.length !== 1 ? 's' : ''}
                    </span>
                    <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                      {active.supporting_signals.map((id) => (
                        <button
                          key={id}
                          className="chip"
                          data-tone="info"
                          onClick={() => setSigId(id)}
                          style={{ cursor: 'pointer' }}
                        >
                          Signal #{id}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <hr style={{ borderColor: 'var(--color-border)', margin: 'var(--space-5) 0' }} />

                <FeedbackForm analysisId={active.id} />
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {sigId !== null && <SignalDetail signalId={sigId} onClose={() => setSigId(null)} />}
    </div>
  );
}
